import { createContext, useCallback, useMemo, useState } from "react";

export const ActivitySetupContext = createContext({
  activity: {},
  setActivity: () => null,
});

// Helper function to prefill default values from inputSchema
export const prefilDefaultValuesInRuleData = (ruleDefinition) => {
  const prefilledValues = {};
  const inputSchema = ruleDefinition?.inputSchema;

  if (inputSchema?.required) {
    inputSchema.required.forEach((propertyKey) => {
      const propertySchema = inputSchema.properties?.[propertyKey];

      if (propertySchema) {
        if (propertySchema.default !== undefined) {
          prefilledValues[propertyKey] = propertySchema.default;
        } else if (propertySchema.type === "object" && propertySchema.properties) {
          const nestedPrefilled = prefilDefaultValuesInRuleData({
            inputSchema: propertySchema,
          });
          if (Object.keys(nestedPrefilled).length > 0) {
            prefilledValues[propertyKey] = nestedPrefilled;
          }
        }
      }
    });
  }

  return Object.keys(prefilledValues).length > 0 ? prefilledValues : {};
};

export const ActivitySetupProvider = ({ activity, setActivity, children }) => {
  const [itemsToRemove, setItemsToRemove] = useState([]);

  const updateActivityRules = useCallback(
    (updatedRules) => {
      setActivity((prev) => ({ ...prev, activityRules: updatedRules }));
    },
    [setActivity]
  );

  const addRule = useCallback(
    (rule) => {
      // Prefill default values from the rule's inputSchema if ruleData is empty
      const ruleDefinition = rule.rule;
      const defaultRuleData = prefilDefaultValuesInRuleData(ruleDefinition);
      const ruleData =
        rule.ruleData && Object.keys(rule.ruleData).length > 0
          ? rule.ruleData
          : defaultRuleData;

      setActivity((prev) => ({
        ...prev,
        activityRules: [
          ...(prev.activityRules || []),
          { ruleData, ruleDefinition, isNew: rule.isNew ?? true },
        ],
      }));
    },
    [setActivity]
  );

  const updateRule = useCallback(
    (data, rule) => {
      const updatedRules = activity?.activityRules.map((ruleItem) =>
        ruleItem === rule ? { ...ruleItem, ruleData: data, isNew: false } : ruleItem
      );
      updateActivityRules(updatedRules);
    },
    [activity?.activityRules, updateActivityRules]
  );

  const updatePackageReceiptRemovedProducts = (initialRules, updatedRules) => {
    const removedRules = initialRules.filter(
      (ruleItem) => !updatedRules.includes(ruleItem)
    );

    const removedRuleData = removedRules[0]?.ruleData?.ids || {};

    if (removedRuleData.length > 0) {
      setItemsToRemove(removedRuleData);
    }
  };

  const activitiesSlug = {
    PACKAGE_SCAN: "package-scan-points",
    RECEIPT_SCAN: "receipt-scan-complete-points",
  };

  const removeRule = useCallback(
    (ruleSlug, occurrence) => {
      const initialActivityRules = activity?.activityRules || [];
      setItemsToRemove([]);

      const updatedRules = activity?.activityRules.reduce(
        (acc, ruleItem, index, arr) => {
          const count = arr
            .slice(0, index + 1)
            .filter((rule) => rule.ruleDefinition.slug === ruleSlug).length;
          return ruleItem.ruleDefinition.slug === ruleSlug && count === occurrence
            ? acc
            : [...acc, ruleItem];
        },
        []
      );

      if (
        activity?.activityDefinition?.slug === activitiesSlug?.PACKAGE_SCAN ||
        activity?.activityDefinition?.slug === activitiesSlug?.RECEIPT_SCAN
      ) {
        updatePackageReceiptRemovedProducts(initialActivityRules, updatedRules);
      }

      updateActivityRules(updatedRules);
    },
    [
      activity?.activityRules,
      activity?.activityDefinition?.slug,
      activitiesSlug?.PACKAGE_SCAN,
      activitiesSlug?.RECEIPT_SCAN,
      updateActivityRules,
    ]
  );

  const value = useMemo(
    () => ({
      activity,
      addRule,
      updateRule,
      removeRule,
      itemsToRemove,
      setItemsToRemove,
    }),
    [activity, addRule, updateRule, removeRule, itemsToRemove]
  );

  return (
    <ActivitySetupContext.Provider value={value}>
      {children}
    </ActivitySetupContext.Provider>
  );
};
